// PDF objects, the lexer / parser, the cross-reference table and indirect-object loading (ISO 32000-1 §7).

import { decodeStream } from "./filters";

export class PName { constructor(public readonly name: string) {} }
export class PRef { constructor(public readonly num: number, public readonly gen: number) {} }
/** An operator of a content stream. */
export class PCmd { constructor(public readonly cmd: string) {} }

export type PObj = null | boolean | number | string | PName | PRef | PCmd | PObj[] | Dict | Stream;

export class Dict {
    readonly map = new Map<string, PObj>();
    constructor(public xref: XRef | null = null) {}
    /** The value with indirect references followed. */
    get(key: string, alt?: string): PObj {
        let v = this.map.get(key);
        if (v === undefined && alt) v = this.map.get(alt);
        return v instanceof PRef && this.xref ? this.xref.fetch(v) : v === undefined ? null : v;
    }
    raw(key: string): PObj | undefined { return this.map.get(key); }
    has(key: string): boolean { return this.map.has(key); }
    set(key: string, v: PObj) { this.map.set(key, v); }
    keys(): string[] { return [...this.map.keys()]; }
    num(key: string, d = 0): number { const v = this.get(key); return typeof v === "number" ? v : d; }
    name(key: string): string | null { const v = this.get(key); return v instanceof PName ? v.name : null; }
    dict(key: string): Dict | null { const v = this.get(key); return v instanceof Dict ? v : v instanceof Stream ? v.dict : null; }
    arr(key: string): PObj[] | null { const v = this.get(key); return Array.isArray(v) ? v : null; }
    str(key: string): string | null { const v = this.get(key); return typeof v === "string" ? v : null; }
}

export class Stream {
    private cache: Uint8Array | null = null;
    constructor(public readonly dict: Dict, public readonly raw: Uint8Array, public readonly ref: PRef | null = null) {}
    /** The data with the filters applied; image filters (DCT, JPX, CCITT, JBIG2) stay for the image decoder. */
    bytes(): Uint8Array {
        if (!this.cache) this.cache = decodeStream(this, false).data;
        return this.cache;
    }
}

export const isName = (o: PObj, n?: string): o is PName => o instanceof PName && (n === undefined || o.name === n);
export const isDict = (o: PObj): o is Dict => o instanceof Dict;
export const isStream = (o: PObj): o is Stream => o instanceof Stream;

const WS = new Uint8Array(256);
const DELIM = new Uint8Array(256);
for (const c of [0, 9, 10, 12, 13, 32]) WS[c] = 1;
for (const c of "()<>[]{}/%") DELIM[c.charCodeAt(0)] = 1;

const latin1 = (b: Uint8Array, s: number, e: number): string => { let r = ""; for (let i = s; i < e; i += 8192) r += String.fromCharCode(...b.subarray(i, Math.min(e, i + 8192))); return r; };
export { latin1 };

export class Lexer {
    constructor(public buf: Uint8Array, public pos = 0, public end = buf.length) {}

    skipWs() {
        const b = this.buf;
        while (this.pos < this.end) {
            const c = b[this.pos]!;
            if (WS[c]) this.pos++;
            else if (c === 0x25) { while (this.pos < this.end && b[this.pos] !== 10 && b[this.pos] !== 13) this.pos++; }
            else break;
        }
    }

    /** The next token: a number, string (latin-1), PName, PCmd for keywords and operators, or the delimiter strings "[" "]" "<<" ">>" "{" "}" as PCmd. null at the end. */
    next(): PObj | undefined {
        this.skipWs();
        const b = this.buf;
        if (this.pos >= this.end) return undefined;
        const c = b[this.pos]!;
        // numbers
        if ((c >= 0x30 && c <= 0x39) || c === 0x2b || c === 0x2d || c === 0x2e) {
            let p = this.pos;
            let sign = 1;
            if (b[p] === 0x2b) p++;
            else if (b[p] === 0x2d) { sign = -1; p++; while (b[p] === 0x2d) p++; }
            let v = 0, any = false;
            while (p < this.end && b[p]! >= 0x30 && b[p]! <= 0x39) { v = v * 10 + (b[p]! - 0x30); p++; any = true; }
            if (p < this.end && b[p] === 0x2e) {
                p++;
                let f = 0.1;
                while (p < this.end && b[p]! >= 0x30 && b[p]! <= 0x39) { v += (b[p]! - 0x30) * f; f /= 10; p++; any = true; }
            }
            if (any || (p > this.pos && (b[this.pos] === 0x2e))) {
                // tolerate "1.2.3" or "12-3": stop at a non-delimiter garbage char
                this.pos = p;
                if (p < this.end && (b[p] === 0x2d)) { while (p < this.end && !WS[b[p]!] && !DELIM[b[p]!]) p++; this.pos = p; }
                return sign * v;
            }
            // a lone sign or dot: a keyword
        }
        switch (c) {
            case 0x28: return this.string();
            case 0x2f: return this.name();
            case 0x5b: this.pos++; return new PCmd("[");
            case 0x5d: this.pos++; return new PCmd("]");
            case 0x7b: this.pos++; return new PCmd("{");
            case 0x7d: this.pos++; return new PCmd("}");
            case 0x3c:
                if (b[this.pos + 1] === 0x3c) { this.pos += 2; return new PCmd("<<"); }
                return this.hex();
            case 0x3e:
                if (b[this.pos + 1] === 0x3e) { this.pos += 2; return new PCmd(">>"); }
                this.pos++;
                return new PCmd(">");
            case 0x29: this.pos++; return new PCmd(")");
        }
        const s = this.pos;
        while (this.pos < this.end && !WS[b[this.pos]!] && !DELIM[b[this.pos]!]) this.pos++;
        if (this.pos === s) this.pos++;
        return new PCmd(latin1(b, s, this.pos));
    }

    private string(): string {
        const b = this.buf;
        this.pos++;
        let depth = 1;
        const out: number[] = [];
        while (this.pos < this.end) {
            let c = b[this.pos++]!;
            if (c === 0x28) { depth++; out.push(c); }
            else if (c === 0x29) { if (--depth === 0) break; out.push(c); }
            else if (c === 0x5c) {
                c = b[this.pos++]!;
                switch (c) {
                    case 0x6e: out.push(10); break;
                    case 0x72: out.push(13); break;
                    case 0x74: out.push(9); break;
                    case 0x62: out.push(8); break;
                    case 0x66: out.push(12); break;
                    case 13: if (b[this.pos] === 10) this.pos++; break;
                    case 10: break;
                    default:
                        if (c >= 0x30 && c <= 0x37) {
                            let v = c - 0x30;
                            for (let k = 0; k < 2 && b[this.pos]! >= 0x30 && b[this.pos]! <= 0x37; k++) v = v * 8 + (b[this.pos++]! - 0x30);
                            out.push(v & 255);
                        } else out.push(c);
                }
            } else out.push(c);
        }
        let r = "";
        for (let i = 0; i < out.length; i += 8192) r += String.fromCharCode(...out.slice(i, i + 8192));
        return r;
    }

    private hex(): string {
        const b = this.buf;
        this.pos++;
        let r = "", hi = -1;
        while (this.pos < this.end) {
            const c = b[this.pos++]!;
            if (c === 0x3e) break;
            const v = c >= 0x30 && c <= 0x39 ? c - 0x30 : c >= 0x41 && c <= 0x46 ? c - 0x37 : c >= 0x61 && c <= 0x66 ? c - 0x57 : -1;
            if (v < 0) continue;
            if (hi < 0) hi = v; else { r += String.fromCharCode(hi * 16 + v); hi = -1; }
        }
        if (hi >= 0) r += String.fromCharCode(hi * 16);
        return r;
    }

    private name(): PName {
        const b = this.buf;
        this.pos++;
        let r = "";
        while (this.pos < this.end && !WS[b[this.pos]!] && !DELIM[b[this.pos]!]) {
            const c = b[this.pos++]!;
            if (c === 0x23 && this.pos + 1 < this.end) {
                const h = parseInt(String.fromCharCode(b[this.pos]!, b[this.pos + 1]!), 16);
                if (!Number.isNaN(h)) { r += String.fromCharCode(h); this.pos += 2; continue; }
            }
            r += String.fromCharCode(c);
        }
        return new PName(r);
    }
}

/** Builds objects (arrays, dictionaries, references, streams) from tokens. */
export class Parser {
    private buf1: PObj | undefined;
    private buf2: PObj | undefined;
    constructor(public lex: Lexer, public xref: XRef | null = null, private allowStreams = false) {
        this.buf1 = lex.next();
        this.buf2 = lex.next();
    }
    private shift() {
        this.buf1 = this.buf2;
        this.buf2 = this.lex.next();
    }

    /** The next object, or undefined at the end of input. A keyword comes back as a PCmd. */
    object(): PObj | undefined {
        const t = this.buf1;
        if (t === undefined) return undefined;
        this.shift();
        if (t instanceof PCmd) {
            if (t.cmd === "[") {
                const a: PObj[] = [];
                while (this.buf1 !== undefined && !(this.buf1 instanceof PCmd && this.buf1.cmd === "]")) {
                    const o = this.object();
                    if (o === undefined) break;
                    a.push(o);
                }
                this.shift();
                return a;
            }
            if (t.cmd === "<<") {
                const d = new Dict(this.xref);
                while (this.buf1 !== undefined && !(this.buf1 instanceof PCmd && this.buf1.cmd === ">>")) {
                    if (!(this.buf1 instanceof PName)) { this.shift(); continue; }
                    const key = this.buf1.name;
                    this.shift();
                    if (this.buf1 instanceof PCmd && this.buf1.cmd === ">>") { d.set(key, null); break; }
                    const v = this.object();
                    d.set(key, v === undefined ? null : v);
                }
                if (this.allowStreams && this.buf1 instanceof PCmd && this.buf1.cmd === ">>" && this.buf2 instanceof PCmd && this.buf2.cmd === "stream") {
                    return this.makeStream(d);
                }
                this.shift();
                return d;
            }
            if (t.cmd === "true") return true;
            if (t.cmd === "false") return false;
            if (t.cmd === "null") return null;
            return t;
        }
        if (typeof t === "number" && Number.isInteger(t) && typeof this.buf1 === "number" && Number.isInteger(this.buf1) && this.buf2 instanceof PCmd && this.buf2.cmd === "R") {
            const ref = new PRef(t, this.buf1);
            this.shift();
            this.shift();
            return ref;
        }
        return t;
    }

    private makeStream(d: Dict): Stream {
        // The lexer is positioned just after the token `stream`: lex.pos is past it, buf1/buf2 hold ">>" and "stream" already consumed lookahead.
        // Re-find the data start from the lexer position: it is right after "stream" because buf2 was the last token read.
        const b = this.lex.buf;
        let p = this.lex.pos;
        if (b[p] === 13) p++;
        if (b[p] === 10) p++;
        let len = -1;
        const l = d.raw("Length");
        if (typeof l === "number") len = l;
        else if (l instanceof PRef && this.xref) { const v = this.xref.fetch(l); if (typeof v === "number") len = v; }
        const end = this.lex.end;
        const endstreamAt = (from: number): number => {
            // the position of "endstream" at or after `from`
            for (let i = from; i + 9 <= end; i++) {
                if (b[i] === 0x65 && b[i + 1] === 0x6e && b[i + 2] === 0x64 && b[i + 3] === 0x73 && b[i + 4] === 0x74 && b[i + 5] === 0x72 && b[i + 6] === 0x65 && b[i + 7] === 0x61 && b[i + 8] === 0x6d) return i;
            }
            return -1;
        };
        let dataEnd = -1;
        if (len >= 0 && p + len <= end) {
            // verify "endstream" follows (after optional whitespace)
            let q = p + len;
            while (q < end && WS[b[q]!]) q++;
            if (b[q] === 0x65 && b[q + 1] === 0x6e && b[q + 2] === 0x64 && b[q + 3] === 0x73) dataEnd = p + len;
        }
        if (dataEnd < 0) {
            const e = endstreamAt(p);
            dataEnd = e < 0 ? end : e;
            // trim one EOL before endstream
            if (b[dataEnd - 1] === 10) dataEnd--;
            if (b[dataEnd - 1] === 13) dataEnd--;
        }
        const s = new Stream(d, b.subarray(p, dataEnd));
        this.lex.pos = dataEnd;
        this.buf1 = this.lex.next();
        this.buf2 = this.lex.next();
        // consume "endstream"
        if (this.buf1 instanceof PCmd && this.buf1.cmd === "endstream") this.shift();
        return s;
    }
}

interface XEntry { type: 0 | 1 | 2; a: number; b: number }

export interface Cipher {
    string(num: number, gen: number, s: string): string;
    stream(num: number, gen: number, d: Uint8Array): Uint8Array;
    /** Streams with /Type /XRef or the encryption dictionary itself are never decrypted. */
}

export class XRef {
    entries = new Map<number, XEntry>();
    trailer = new Dict(this);
    private cache = new Map<number, PObj>();
    private loading = new Set<number>();
    cipher: Cipher | null = null;
    private objStms = new Map<number, Array<[number, PObj]>>();

    constructor(public readonly data: Uint8Array) {}

    clearCache() { this.cache.clear(); this.objStms.clear(); }

    /** Reads the cross-reference data; falls back to scanning the file when it is damaged. */
    open() {
        let ok = false;
        try { ok = this.readFromEnd(); } catch { ok = false; }
        if (ok) {
            try { const root = this.trailer.get("Root"); ok = root instanceof Dict && root.get("Pages") instanceof Dict; } catch { ok = false; }
        }
        if (!ok) this.rebuild();
    }

    private findStartXref(): number {
        const b = this.data;
        const from = Math.max(0, b.length - 2048);
        for (let i = b.length - 9; i >= from; i--) {
            if (b[i] === 0x73 && b[i + 1] === 0x74 && b[i + 2] === 0x61 && b[i + 3] === 0x72 && b[i + 4] === 0x74 && b[i + 5] === 0x78 && b[i + 6] === 0x72 && b[i + 7] === 0x65 && b[i + 8] === 0x66) {
                const lx = new Lexer(b, i + 9);
                const n = lx.next();
                return typeof n === "number" ? n : -1;
            }
        }
        return -1;
    }

    private readFromEnd(): boolean {
        let pos = this.findStartXref();
        if (pos < 0) return false;
        // the header may be preceded by junk: offsets are relative to "%PDF-"
        const seen = new Set<number>();
        let first = true;
        const queue: number[] = [pos];
        while (queue.length) {
            pos = queue.shift()!;
            if (seen.has(pos) || pos < 0 || pos >= this.data.length) continue;
            seen.add(pos);
            const tr = this.readSection(pos);
            if (!tr) { if (first) return false; continue; }
            if (first) { for (const k of tr.keys()) if (k !== "Prev" && k !== "XRefStm") this.trailer.set(k, tr.raw(k)!); first = false; }
            else for (const k of tr.keys()) if (!this.trailer.has(k) && k !== "Prev" && k !== "XRefStm") this.trailer.set(k, tr.raw(k)!);
            const xs = tr.raw("XRefStm");
            if (typeof xs === "number") queue.unshift(xs);
            const prev = tr.raw("Prev");
            if (typeof prev === "number") queue.push(prev);
        }
        return this.entries.size > 0 && this.trailer.has("Root");
    }

    private readSection(pos: number): Dict | null {
        const lx = new Lexer(this.data, pos);
        lx.skipWs();
        const b = this.data;
        if (b[lx.pos] === 0x78 && b[lx.pos + 1] === 0x72 && b[lx.pos + 2] === 0x65 && b[lx.pos + 3] === 0x66) {
            lx.pos += 4;
            for (;;) {
                const save = lx.pos;
                const a = lx.next();
                if (a instanceof PCmd && a.cmd === "trailer") break;
                const c = lx.next();
                if (typeof a !== "number" || typeof c !== "number") { lx.pos = save; return null; }
                let start = a;
                for (let i = 0; i < c; i++) {
                    const off = lx.next(), gen = lx.next(), kind = lx.next();
                    if (typeof off !== "number" || typeof gen !== "number" || !(kind instanceof PCmd)) return null;
                    const num = start + i;
                    if (!this.entries.has(num)) this.entries.set(num, kind.cmd === "n" ? { type: 1, a: off, b: gen } : { type: 0, a: off, b: gen });
                }
                start = 0;
            }
            const p = new Parser(lx, this);
            const t = p.object();
            return t instanceof Dict ? t : null;
        }
        // a cross-reference stream: "n g obj << ... >> stream"
        const p = new Parser(lx, this, true);
        const n = p.object(), g = p.object(), o = p.object();
        if (typeof n !== "number" || typeof g !== "number" || !(o instanceof PCmd && o.cmd === "obj")) return null;
        const s = p.object();
        if (!(s instanceof Stream)) return null;
        const d = s.dict;
        const w = d.arr("W");
        if (!w) return null;
        const [w0, w1, w2] = w.map((x) => (typeof x === "number" ? x : 0)) as [number, number, number];
        const size = d.num("Size");
        const index = (d.arr("Index") ?? [0, size]) as number[];
        const data = s.bytes();
        let off = 0;
        const rd = (n: number) => { let v = 0; for (let i = 0; i < n; i++) v = v * 256 + (data[off++] ?? 0); return v; };
        for (let k = 0; k + 1 < index.length; k += 2) {
            for (let i = 0; i < index[k + 1]!; i++) {
                const type = w0 ? rd(w0) : 1, f2 = rd(w1), f3 = rd(w2);
                const num = index[k]! + i;
                if (this.entries.has(num)) continue;
                if (type === 1) this.entries.set(num, { type: 1, a: f2, b: f3 });
                else if (type === 2) this.entries.set(num, { type: 2, a: f2, b: f3 });
                else this.entries.set(num, { type: 0, a: f2, b: f3 });
            }
        }
        return d;
    }

    /** Scans the whole file for "n g obj" markers and trailers. */
    private rebuild() {
        this.entries.clear();
        this.cache.clear();
        const b = this.data;
        const n = b.length;
        let trailer: Dict | null = null;
        let i = 0;
        const isDigit = (c: number) => c >= 0x30 && c <= 0x39;
        while (i < n) {
            const c = b[i]!;
            if (isDigit(c) && (i === 0 || WS[b[i - 1]!] || DELIM[b[i - 1]!])) {
                let j = i;
                while (j < n && isDigit(b[j]!)) j++;
                let k = j;
                while (k < n && (b[k] === 32 || b[k] === 9)) k++;
                if (k > j && isDigit(b[k]!)) {
                    let m = k;
                    while (m < n && isDigit(b[m]!)) m++;
                    let q = m;
                    while (q < n && (b[q] === 32 || b[q] === 9)) q++;
                    if (b[q] === 0x6f && b[q + 1] === 0x62 && b[q + 2] === 0x6a && (q + 3 >= n || !(b[q + 3]! >= 0x61 && b[q + 3]! <= 0x7a))) {
                        const num = parseInt(latin1(b, i, j), 10), gen = parseInt(latin1(b, k, m), 10);
                        this.entries.set(num, { type: 1, a: i, b: gen });
                        i = q + 3;
                        continue;
                    }
                }
                i = j;
                continue;
            }
            if (c === 0x74 && b[i + 1] === 0x72 && b[i + 2] === 0x61 && b[i + 3] === 0x69 && b[i + 4] === 0x6c && b[i + 5] === 0x65 && b[i + 6] === 0x72) {
                try {
                    const p = new Parser(new Lexer(b, i + 7), this);
                    const t = p.object();
                    if (t instanceof Dict && (t.has("Root") || !trailer)) { if (!trailer) trailer = t; else for (const kk of t.keys()) trailer.set(kk, t.raw(kk)!); }
                } catch { /* skip */ }
                i += 7;
                continue;
            }
            i++;
        }
        // object streams and xref streams carry their own entries / trailers
        const nums = [...this.entries.keys()];
        for (const num of nums) {
            const e = this.entries.get(num)!;
            if (e.type !== 1) continue;
            try {
                const o = this.fetch(new PRef(num, e.b));
                if (o instanceof Stream) {
                    const t = o.dict.name("Type");
                    if (t === "XRef" && !trailer?.has("Root") && o.dict.has("Root")) { trailer = trailer ?? new Dict(this); for (const kk of o.dict.keys()) if (!trailer.has(kk)) trailer.set(kk, o.dict.raw(kk)!); }
                    if (t === "ObjStm") {
                        const items = this.readObjStm(num);
                        items.forEach(([on], idx) => { if (!this.entries.has(on) || this.entries.get(on)!.type !== 1) this.entries.set(on, { type: 2, a: num, b: idx }); });
                    }
                }
            } catch { /* skip */ }
        }
        this.trailer = trailer ?? new Dict(this);
        this.trailer.xref = this;
        if (!this.trailer.has("Root")) {
            // find a catalog
            for (const num of this.entries.keys()) {
                try {
                    const o = this.fetch(new PRef(num, 0));
                    if (o instanceof Dict && o.name("Type") === "Catalog") { this.trailer.set("Root", new PRef(num, this.entries.get(num)!.type === 1 ? this.entries.get(num)!.b : 0)); break; }
                } catch { /* skip */ }
            }
        }
    }

    private readObjStm(num: number): Array<[number, PObj]> {
        const hit = this.objStms.get(num);
        if (hit) return hit;
        const e = this.entries.get(num);
        const st = this.fetch(new PRef(num, e?.type === 1 ? e.b : 0));
        if (!(st instanceof Stream)) return [];
        const n = st.dict.num("N"), first = st.dict.num("First");
        const data = st.bytes();
        const lx = new Lexer(data, 0);
        const nums: Array<[number, number]> = [];
        for (let i = 0; i < n; i++) {
            const on = lx.next(), of = lx.next();
            if (typeof on !== "number" || typeof of !== "number") break;
            nums.push([on, of]);
        }
        const out: Array<[number, PObj]> = [];
        for (const [on, of] of nums) {
            const p = new Parser(new Lexer(data, first + of), this);
            const o = p.object();
            out.push([on, o === undefined ? null : o]);
        }
        this.objStms.set(num, out);
        return out;
    }

    /** Loads an indirect object (cached). Unknown or broken references are null. */
    fetch(ref: PRef): PObj {
        const c = this.cache.get(ref.num);
        if (c !== undefined) return c;
        const e = this.entries.get(ref.num);
        if (!e || e.type === 0) return null;
        if (this.loading.has(ref.num)) return null;
        this.loading.add(ref.num);
        let result: PObj = null;
        try {
            if (e.type === 2) {
                const items = this.readObjStm(e.a);
                const hit = items[e.b]?.[0] === ref.num ? items[e.b] : items.find((x) => x[0] === ref.num);
                result = hit ? hit[1] : null;
            } else {
                result = this.readIndirect(e.a, ref);
            }
        } catch { result = null; }
        finally { this.loading.delete(ref.num); }
        this.cache.set(ref.num, result);
        return result;
    }

    private readIndirect(offset: number, ref: PRef): PObj {
        const lx = new Lexer(this.data, offset);
        const p = new Parser(lx, this, true);
        const n = p.object(), g = p.object(), o = p.object();
        if (typeof n !== "number" || typeof g !== "number" || !(o instanceof PCmd && o.cmd === "obj")) {
            if (!this.rebuilt) { this.rebuilt = true; this.rebuild(); return this.fetch(ref); }
            return null;
        }
        if (n !== ref.num && !this.rebuilt) { this.rebuilt = true; this.rebuild(); return this.fetch(ref); }
        let obj = p.object();
        if (obj === undefined) obj = null;
        if (obj instanceof PCmd) obj = null; // "endobj" right away: a null object
        return this.decryptObj(obj, ref.num, g);
    }
    private rebuilt = false;

    private decryptObj(o: PObj, num: number, gen: number): PObj {
        const c = this.cipher;
        if (!c) return o;
        const walk = (v: PObj): PObj => {
            if (typeof v === "string") return c.string(num, gen, v);
            if (Array.isArray(v)) return v.map(walk);
            if (v instanceof Dict) { for (const k of v.keys()) v.map.set(k, walk(v.raw(k)!)); return v; }
            return v;
        };
        if (o instanceof Stream) {
            const t = o.dict.name("Type");
            if (t === "XRef") return o;
            walk(o.dict);
            const bytes = c.stream(num, gen, o.raw);
            return new Stream(o.dict, bytes, new PRef(num, gen));
        }
        return walk(o);
    }

    /** Resolves a reference, or returns the value itself. */
    resolve(o: PObj | undefined): PObj {
        return o instanceof PRef ? this.fetch(o) : o === undefined ? null : o;
    }
}

/** Parses the objects of an in-memory content or data buffer (no xref). */
export function parseObjects(data: Uint8Array, xref: XRef | null = null): PObj[] {
    const p = new Parser(new Lexer(data), xref);
    const out: PObj[] = [];
    for (;;) { const o = p.object(); if (o === undefined) break; out.push(o); }
    return out;
}
